import React from 'react';
import { Users, Clock, FileText, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';


export default function MemberManagement() {
  return (
    <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter']">

      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#9B1421]/20 to-[#7A0F1A]/10 py-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <div className="inline-block bg-[#9B1421]/20 p-4 rounded-full mb-6">
              <Users className="w-12 h-12 text-rollops-accent mx-auto" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Member Management That <span className="text-rollops-accent">Actually Works</span>
            </h1>
            <p className="text-xl md:text-2xl text-[#A9A3A3] mb-8 max-w-2xl mx-auto">
              Stop juggling spreadsheets and sticky notes. Start building real relationships with your members.
            </p>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-16 md:py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-lg md:text-xl text-[#A9A3A3] leading-relaxed mb-6">
                We&apos;ve been where you are. Running a gym means wearing a hundred different hats - coach, 
                administrator, marketer, accountant. The last thing you need is software that makes your life harder.
              </p>
              <p className="text-lg md:text-xl text-[#A9A3A3] leading-relaxed mb-6">
                RollOps was built by gym owners, for gym owners. We know that every member has a story, 
                every enrollment matters, and every interaction counts. That&apos;s why we&apos;ve created a member 
                management system that gets out of your way and lets you focus on what you do best - changing lives.
              </p>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 md:py-20 px-4 bg-[#1A1818]">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Everything You Need, Right Where You Need It
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Users className="w-6 h-6 text-rollops-accent" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Complete Member Profiles</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      See everything about each member in one place - contact info, enrollment history, 
                      attendance patterns, payment status, and notes. No more hunting through multiple systems.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Clock className="w-6 h-6 text-rollops-accent" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Real-Time Attendance Tracking</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Know who&apos;s in your gym right now. Track attendance automatically with geofencing, 
                      or check members in manually. See attendance patterns and identify members who need a check-in.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <FileText className="w-6 h-6 text-rollops-accent" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Digital Waivers & Forms</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Collect waivers, emergency contacts, and other forms digitally. Everything is stored securely 
                      and accessible instantly. No more filing cabinets full of paper.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#221F1F] p-8 rounded-lg border border-[#504A4A]">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Shield className="w-6 h-6 text-rollops-accent" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3">Secure & Private</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Your members&apos; data is protected with bank-level security. We&apos;re HIPAA-compliant 
                      and take privacy seriously. Your members trust you - we help you honor that trust.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Member Management?
            </h2>
            <p className="text-xl text-[#A9A3A3] mb-8">
              Join gym owners who are already saving hours every week with RollOps.
            </p>
            <Link
              to="/founders-promo"
              className="inline-block bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Get Started Today
            </Link>
          </div>
        </section>
      </main>

    </div>
  );
}
